// globalerHinweis.tsx — K3: ein Meldungskanal, der NICHT am Fenster hängt.
//
// `useHinweisToast` (HinweisToast.tsx) ist lokaler Component-State und stirbt
// mit dem Fenster. Genau das passiert im „Neu erstellen"-Zweig: der
// TerminLightDialog schließt sich unmittelbar nach `openVorgangCreateFlow`
// selbst, während der Bind erst danach läuft — ein `setError` dort erreicht
// niemanden. Deshalb hier dasselbe Muster wie bei `requestCalendarReload()`:
// eine modul-globale Subscriber-Liste, und genau EIN Toast, der im WindowLayer
// gemountet ist und damit über allen Fenstern liegt.
//
// `art:'info'` blendet nach ein paar Sekunden aus; `art:'fehler'` bleibt
// stehen, bis der Nutzer wegklickt — eine fehlgeschlagene Verknüpfung darf
// nicht davonhuschen.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { REFINED } from "../risikoanalyseStep/tokens";

export type HinweisArt = "info" | "fehler";

export interface GlobalerHinweis {
    id: number;
    text: string;
    art: HinweisArt;
}

/** Anzeigedauer der Info-Variante; Fehler bleiben stehen. */
const INFO_DAUER_MS = 4000;

type Abonnent = (h: GlobalerHinweis) => void;

const abonnenten = new Set<Abonnent>();
let laufendeId = 0;

/**
 * Zeigt eine Meldung an — unabhängig davon, ob das auslösende Fenster noch
 * gemountet ist. Ohne Abonnenten (z. B. in einem Test ohne WindowLayer)
 * passiert schlicht nichts; ein Fehler wäre hier das schlechtere Verhalten.
 */
export function zeigeGlobalenHinweis(text: string, opts?: { art?: HinweisArt }): void {
    const hinweis: GlobalerHinweis = {
        id: ++laufendeId,
        text,
        art: opts?.art ?? "info",
    };
    for (const fn of abonnenten) {
        try {
            fn(hinweis);
        } catch {
            // ein fehlerhafter Abonnent darf die übrigen nicht blockieren
        }
    }
}

/** Abonniert die Meldungen; der Rückgabewert bestellt wieder ab. */
export function abonniereHinweis(cb: Abonnent): () => void {
    abonnenten.add(cb);
    return () => {
        abonnenten.delete(cb);
    };
}

/**
 * Der EINE Toast. Gehört genau einmal in den WindowLayer — der ist über
 * NavigationLayout immer gemountet und liegt über allen Fenstern.
 */
export const GlobalerHinweisToast: React.FC = () => {
    const [hinweis, setHinweis] = useState<GlobalerHinweis | null>(null);
    const timerRef = useRef<number | null>(null);

    const abraeumen = useCallback(() => {
        if (timerRef.current != null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    useEffect(() => {
        const unsub = abonniereHinweis((h) => {
            abraeumen();
            setHinweis(h);
            if (h.art === "info") {
                timerRef.current = window.setTimeout(() => {
                    timerRef.current = null;
                    setHinweis(null);
                }, INFO_DAUER_MS);
            }
        });
        return () => {
            unsub();
            abraeumen();
        };
    }, [abraeumen]);

    if (!hinweis) return null;
    const istFehler = hinweis.art === "fehler";

    return (
        <div
            role="status"
            aria-live={istFehler ? "assertive" : "polite"}
            style={{
                ...toastStyle,
                background: istFehler ? "#7a2020" : REFINED.ink,
            }}
        >
            <span style={{ flex: 1 }}>{hinweis.text}</span>
            {istFehler && (
                <button
                    type="button"
                    onClick={() => {
                        abraeumen();
                        setHinweis(null);
                    }}
                    aria-label="Meldung schließen"
                    style={schliessenStyle}
                >
                    ✕
                </button>
            )}
        </div>
    );
};

const toastStyle: React.CSSProperties = {
    position: "fixed",
    left: "50%",
    bottom: 28,
    transform: "translateX(-50%)",
    maxWidth: "min(92vw, 560px)",
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 14px 10px 18px",
    borderRadius: 10,
    color: REFINED.white,
    font: `600 13px ${REFINED.fontUI}`,
    boxShadow: "0 6px 24px rgba(0, 0, 0, 0.25)",
    zIndex: 100001,
    textAlign: "left",
};

const schliessenStyle: React.CSSProperties = {
    border: "none",
    background: "rgba(255, 255, 255, 0.16)",
    color: "inherit",
    borderRadius: 6,
    width: 24,
    height: 24,
    lineHeight: "24px",
    cursor: "pointer",
    font: "inherit",
    padding: 0,
};

export default GlobalerHinweisToast;
